<template>
  <UPopover
    v-model:open="isOpen"
    :content="filterContent"
    :ui="filterUi"
  >
    <button
      type="button"
      class="-ml-px border cursor-pointer flex focus:outline-none focus:z-(--z-index-ds-lifted) items-center px-4 py-2 relative space-x-2 transition-colors"
      :class="[
        hasDateTimeSelected
          ? 'z-(--z-index-ds-raised) border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 dark:border-primary/40 dark:bg-primary/20 dark:hover:bg-primary/25'
          : 'border-border-level-200 bg-surface-canvas text-content-level-700 hover:bg-surface-level-50 dark:border-border-inverse/10 dark:bg-surface-clear dark:text-content-level-300 dark:hover:bg-surface-canvas/5'
      ]"
    >
      <CalendarIcon class="shrink-0 size-4" />

      <span class="font-medium text-sm">{{ currentDateTimeText }}</span>

      <ChevronDownIcon class="shrink-0 size-4" />
    </button>

    <template #content>
      <div class="p-6">
        <!-- Responsive Grid Layout -->
        <div class="gap-6 grid grid-cols-1 lg:grid-cols-2">
          <!-- Delivery Date Section -->
          <div>
            <h3 class="dark:text-content-inverse font-semibold mb-4 text-content-level-900 text-lg">
              {{ $t('commerce.dateTimeFilter.eventDate') }}
            </h3>

            <div
              class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 p-3 rounded-lg"
            >
              <div class="flex items-center">
                <h2
                  class="dark:text-content-inverse flex-auto font-semibold text-content-level-900 text-sm"
                >
                  {{ currentMonthYear }}
                </h2>

                <button
                  type="button"
                  class="-my-1.5 dark:hover:text-content-inverse dark:text-content-level-400 flex flex-none hover:text-content-level-500 items-center justify-center p-1.5 text-content-level-400"
                  @click="previousMonth"
                >
                  <span class="sr-only">
                    {{ $t('commerce.dateTimeFilter.previousMonth') }}
                  </span>

                  <ChevronLeftIcon
                    class="size-4"
                    aria-hidden="true"
                  />
                </button>

                <button
                  type="button"
                  class="-mr-1.5 -my-1.5 dark:hover:text-content-inverse dark:text-content-level-400 flex flex-none hover:text-content-level-500 items-center justify-center ml-2 p-1.5 text-content-level-400"
                  @click="nextMonth"
                >
                  <span class="sr-only">
                    {{ $t('commerce.dateTimeFilter.nextMonth') }}
                  </span>

                  <ChevronRightIcon
                    class="size-4"
                    aria-hidden="true"
                  />
                </button>
              </div>

              <div
                class="dark:text-content-level-400 grid grid-cols-7 mt-6 text-center text-content-level-500 text-xs/6"
              >
                <div>
                  {{ $t('commerce.dateTimeFilter.m') }}
                </div>

                <div>
                  {{ $t('commerce.dateTimeFilter.t') }}
                </div>

                <div>
                  {{ $t('commerce.dateTimeFilter.w') }}
                </div>

                <div>
                  {{ $t('commerce.dateTimeFilter.t') }}
                </div>

                <div>
                  {{ $t('commerce.dateTimeFilter.f') }}
                </div>

                <div>
                  {{ $t('commerce.dateTimeFilter.s') }}
                </div>

                <div>
                  {{ $t('commerce.dateTimeFilter.s') }}
                </div>
              </div>

              <div class="grid grid-cols-7 mt-1 text-sm">
                <div
                  v-for="(day, dayIdx) in calendarDays"
                  :key="day.date"
                  :data-first-line="dayIdx <= 6 ? '' : undefined"
                  class="dark:not-data-first-line:border-border-inverse/10 not-data-first-line:border-border-level-200 not-data-first-line:border-t py-1"
                >
                  <button
                    type="button"
                    :data-is-selected="day.isSelected ? '' : undefined"
                    :data-is-current-month="day.isCurrentMonth ? '' : undefined"
                    :data-is-today="day.isToday ? '' : undefined"
                    class="flex items-center justify-center mx-auto rounded-full size-7 transition-colors"
                    :class="
                      day.isPast
                        ? 'text-content-level-300 dark:text-content-level-600 cursor-not-allowed pointer-events-none'
                        : 'cursor-pointer not-data-is-selected:not-data-is-today:not-data-is-current-month:text-content-level-400 not-data-is-selected:hover:bg-surface-level-200 not-data-is-selected:not-data-is-today:data-is-current-month:text-content-level-900 data-is-selected:font-semibold data-is-selected:text-content-inverse data-is-selected:not-data-is-today:bg-primary data-is-today:font-semibold not-data-is-selected:data-is-today:text-primary data-is-selected:data-is-today:bg-primary dark:not-data-is-selected:not-data-is-today:not-data-is-current-month:!text-content-inverse dark:not-data-is-selected:hover:bg-surface-canvas/10 dark:not-data-is-selected:not-data-is-today:data-is-current-month:!text-content-inverse dark:data-is-selected:not-data-is-today:bg-primary dark:data-is-selected:not-data-is-today:!text-content-level-900 dark:not-data-is-selected:data-is-today:!text-content-inverse dark:data-is-selected:data-is-today:bg-primary dark:data-is-selected:data-is-today:!text-content-level-900'
                    "
                    @click="() => !day.isPast && selectDate(day)"
                  >
                    <time :datetime="day.date">{{ day.date.slice(-2).replace(/^0/, '') }}</time>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Delivery Time Section -->
          <div>
            <h3 class="dark:text-content-inverse font-semibold mb-4 text-content-level-900 text-lg">
              {{ $t('commerce.dateTimeFilter.eventTime') }}
            </h3>

            <p class="dark:text-content-level-400 mb-6 text-content-level-600 text-sm">
              {{ $t('commerce.dateTimeFilter.selectATimeForYourOrder') }}
            </p>

            <!-- Time Slots -->
            <div class="space-y-3">
              <button
                :disabled="!canGoEarlier"
                type="button"
                class="border focus:outline-none font-medium px-3 py-2 rounded-lg text-center text-sm transition-colors w-full"
                :class="
                  canGoEarlier
                    ? 'border-border-level-300 dark:border-border-level-600 hover:bg-surface-level-50 dark:hover:bg-surface-level-700 hover:border-border-level-400 dark:hover:border-border-level-500 text-content-level-900 dark:text-content-inverse cursor-pointer'
                    : 'text-content-level-400 border-border-level-300 dark:border-border-level-600 cursor-not-allowed'
                "
                @click="goEarlier"
              >
                {{ $t('commerce.dateTimeFilter.earlier') }}
              </button>

              <div class="gap-2 grid grid-cols-4">
                <button
                  v-for="time in timeSlots"
                  :key="time"
                  type="button"
                  class="border cursor-pointer focus:outline-none p-3 rounded-lg text-sm transition-colors"
                  :class="
                    selectedTime === time
                      ? 'border-primary bg-primary text-content-inverse font-medium shadow-sm'
                      : 'border-border-level-300 dark:border-border-level-600 text-content-level-900 dark:text-content-inverse'
                  "
                  @click="() => selectTime(time)"
                >
                  {{ time }}
                </button>
              </div>

              <p
                v-if="hasNoAvailableTimes"
                class="bg-warning-50 dark:bg-warning-500/10 dark:text-warning-300 px-3 py-4 rounded-lg text-center text-sm text-warning-800"
              >
                {{ $t('commerce.dateTimeFilter.noTimesAreAvailableForThisDate') }}
              </p>

              <button
                :disabled="!canGoLater"
                type="button"
                class="border focus:outline-none font-medium px-3 py-2 rounded-lg text-center text-sm transition-colors w-full"
                :class="
                  canGoLater
                    ? 'border-border-level-300 dark:border-border-level-600 hover:bg-surface-level-50 dark:hover:bg-surface-level-700 hover:border-border-level-400 dark:hover:border-border-level-500 text-content-level-900 dark:text-content-inverse cursor-pointer'
                    : 'text-content-level-400 border-border-level-300 dark:border-border-level-600 cursor-not-allowed'
                "
                @click="goLater"
              >
                {{ $t('commerce.dateTimeFilter.later') }}
              </button>
            </div>

            <p
              v-if="validationError"
              class="mt-3 text-danger text-sm"
            >
              {{ validationError }}
            </p>
          </div>
        </div>

        <!-- Info Message -->
        <div class="flex items-start mb-6 mt-6 space-x-2">
          <div
            class="bg-info-100 dark:bg-info-900 flex flex-shrink-0 h-5 items-center justify-center rounded-full w-5"
          >
            <span class="dark:text-info-400 font-bold text-info-600 text-xs">{{
              $t('commerce.dateTimeFilter.i')
            }}</span>
          </div>

          <p class="dark:text-content-level-400 text-content-level-600 text-sm">
            {{ $t('commerce.dateTimeFilter.eventDateHelpsUsShowYouOnly') }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end space-x-3">
          <button
            v-if="hasDateTimeSelected"
            type="button"
            class="bg-surface-canvas border border-border-level-300 cursor-pointer dark:bg-surface-clear dark:border-border-inverse/20 dark:hover:bg-surface-canvas/10 dark:text-content-level-200 font-medium hover:bg-surface-level-50 px-4 py-2 rounded-md text-content-level-700 text-sm"
            @click="clear"
          >
            {{ $t('commerce.dateTimeFilter.clear') }}
          </button>

          <button
            type="button"
            :disabled="!canApplySelection"
            class="bg-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 font-medium hover:bg-primary/90 px-6 py-2 rounded-md text-content-inverse text-sm"
            @click="apply"
          >
            {{ $t('commerce.dateTimeFilter.update') }}
          </button>
        </div>
      </div>
    </template>
  </UPopover>
</template>

<script setup lang="ts">
import UPopover from '@nuxt/ui/components/Popover.vue'
import { CalendarIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
import { useEventDateTimeSelection } from '~/composables/useEventDateTimeSelection'
import { useMonthCalendar } from '~/composables/useMonthCalendar'
import { parseEventAvailabilityMenus } from '~/utils/parseEventAvailabilityMenus'
import { overlayContentClasses } from '~/utils/overlayContentClasses'
import { convertTo12Hour } from '~/utils/convertTo12Hour'
import type { EventDateTime } from '~/interfaces/EventDateTime'
import type { EventDateTimeSelectionSource } from '~/interfaces/EventDateTimeSelectionSource'
import type { OrderType } from '~/enums/order/OrderType'
import type { TimeSlotStore } from '~/utils/TimeSlotStore'

const props = withDefaults(
  defineProps<{
    /** Current event date used to seed the availability filter. */
    currentEventDate?: string | null

    /** Current event time used to seed the availability filter. */
    currentEventTime?: string | null

    /** Timezone used when interpreting event date and time values. */
    timezone?: string | null

    /** Order type used to filter available menus. */
    orderType?: OrderType | null

    /** Store whose hours and availability inform the filter. */
    store?: TimeSlotStore | null

    /** Menus available for the selected store and event. */
    menus?: unknown[]
  }>(),
  {
    currentEventDate: null,
    currentEventTime: null,
    menus: () => [],
    orderType: null,
    store: null,
    timezone: null
  }
)

const emit = defineEmits<{
  'update:eventDateTime': [value: EventDateTime]
}>()

const isOpen = ref(false)

const filterContent = { align: 'center', sideOffset: 12 } as const

const filterUi = {
  content: [
    overlayContentClasses.frame,
    overlayContentClasses.panel,
    'max-w-5xl overflow-hidden text-sm/6 w-screen'
  ].join(' ')
} as const

const selectionSource: EventDateTimeSelectionSource = reactive({
  currentEventDate: toRef(props, 'currentEventDate'),
  currentEventTime: toRef(props, 'currentEventTime'),
  menus: computed(() => parseEventAvailabilityMenus(props.menus)),
  orderType: toRef(props, 'orderType'),
  store: toRef(props, 'store'),
  timezone: toRef(props, 'timezone')
})

const {
  apply,
  canApplySelection,
  canGoEarlier,
  canGoLater,
  clear,
  effectiveTimezone,
  goEarlier,
  goLater,
  hasNoAvailableTimes,
  selectDate,
  selectedCalendarDate,
  selectedTime,
  selectTime,
  timeSlots,
  validationError
} = useEventDateTimeSelection(selectionSource, (value) => {
  emit('update:eventDateTime', value)
  isOpen.value = false
})

const { calendarDays, currentMonthYear, nextMonth, previousMonth } = useMonthCalendar({
  isSelected: (date) => selectedCalendarDate.value === date,
  timezone: effectiveTimezone
})

const hasDateTimeSelected = computed(
  () => (props.currentEventDate ?? '') !== '' || (props.currentEventTime ?? '') !== ''
)

const currentDateTimeText = computed(() => {
  const eventDate = props.currentEventDate ?? ''
  const formattedTime = convertTo12Hour(props.currentEventTime ?? '')
  if (eventDate === '') return formattedTime === '' ? 'Date & Time' : formattedTime

  // Parse date without timezone conversion
  const [year = '0', month = '0', day = '0'] = eventDate.split('-')
  const formattedDate = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day)
  ).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
  return formattedTime === '' ? formattedDate : `${formattedDate} at ${formattedTime}`
})

defineExpose({
  closePopover: () => {
    isOpen.value = false
  },
  openPopover: () => {
    isOpen.value = true
  }
})
</script>
